import type { Dispatch, SetStateAction } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

/**
 * The tip a guest has picked, held outside the drawer that picks it.
 *
 * The tip drawer unmounts the moment the guest steps forward to the payment
 * method, so anything it kept in component state was gone on the way back and
 * the amount reset to zero. This is the source of truth instead.
 *
 * Persisted, like the cart and the points, so it also survives a reload or the
 * app being backgrounded mid-payment. It is cleared in exactly two places, and
 * nowhere else:
 *   - the payment went through, so the tip has been taken
 *   - the guest stepped back before the tip, to how the bill is split
 */
interface TipDraftState {
  /** The percentage chip, or the custom label, exactly as the drawer holds it. */
  selectedTip: string;
  customTipValue: string;
  setSelectedTip: (selectedTip: string) => void;
  /** Accepts the updater form: TransparentTextField drives it like useState. */
  setCustomTipValue: Dispatch<SetStateAction<string>>;
  clear: () => void;
}

/**
 * What a fresh tip starts at. A tip screen that opens at zero reads as "no
 * tip unless you insist"; this asks the question the other way round, and 0%
 * is one tap away.
 */
export const TIP_PERCENTAGES = [0, 10, 15, 20];
export const DEFAULT_TIP_PERCENT = 15;

const empty = {
  selectedTip: `${DEFAULT_TIP_PERCENT}%`,
  customTipValue: "",
};

export const useTipDraftStore = create<TipDraftState>()(
  persist(
    (set) => ({
      ...empty,
      setSelectedTip: (selectedTip) => set({ selectedTip }),
      setCustomTipValue: (customTipValue) =>
        set((state) => ({
          customTipValue:
            typeof customTipValue === "function"
              ? customTipValue(state.customTipValue)
              : customTipValue,
        })),
      clear: () => set({ ...empty }),
    }),
    {
      name: "tip-draft",
      version: 3,
      partialize: (state) => ({
        selectedTip: state.selectedTip,
        customTipValue: state.customTipValue,
      }),
      // Older drafts kept a tip target, which the checkout no longer asks for:
      // the server tips whoever is serving the table. One that never chose an
      // amount takes the default rather than staying at nothing.
      migrate: (persisted, version) => {
        if (version === 3) return persisted as typeof empty;
        if (version === 1 || version === 2) {
          const draft = persisted as typeof empty & { tipUserId?: unknown };
          return {
            selectedTip: draft.selectedTip || empty.selectedTip,
            customTipValue: draft.customTipValue ?? "",
          };
        }
        return undefined;
      },
    },
  ),
);

export const useTipDraft = () =>
  useTipDraftStore(
    useShallow((state) => ({
      selectedTip: state.selectedTip,
      customTipValue: state.customTipValue,
      setSelectedTip: state.setSelectedTip,
      setCustomTipValue: state.setCustomTipValue,
    })),
  );

/** For the two places that end a tip. */
export const clearTipDraft = () => useTipDraftStore.getState().clear();
